import { useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { MAX_BODY_LENGTH } from '@sokdaq/protocol';
import { platform } from '../platform/bridge.js';
import { useChatStore } from '../store/chat.js';
import { useUiStore } from '../store/ui.js';

/**
 * 업무 툴 위장.
 *
 * 대화가 스프레드시트 셀로 렌더링된다. 이 화면의 목표는 "흘긋 보면
 * 스프레드시트"이므로, 여기서는 말풍선·아바타·앱 이름 같은 채팅 흔적을
 * 일절 쓰지 않는다. 안내 문구도 띄우지 않는다 — 그게 제일 먼저 눈에 띈다.
 */

/** 화면을 채우기 위해 뒤에 붙이는 빈 행. 진짜 시트처럼 보이게 한다. */
const FILLER_ROWS = 40;

/** 두 번째 Esc를 이 시간 안에 누르면 패닉으로 본다. */
const PANIC_WINDOW_MS = 600;

const COLUMNS = ['시각', '담당', '비고'] as const;

export function StealthView() {
  const messages = useChatStore((s) => s.messages);
  const members = useChatStore((s) => s.members);
  const status = useChatStore((s) => s.status);
  const sendText = useChatStore((s) => s.sendText);
  const sendWhisper = useChatStore((s) => s.sendWhisper);
  const toggleStealth = useUiStore((s) => s.toggleStealth);

  const [draft, setDraft] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const inputRowRef = useRef<HTMLTableRowElement>(null);
  const lastEscapeAt = useRef(0);
  const stickToBottom = useRef(true);

  const nicknames = useMemo(
    () => new Map(members.map((m) => [m.userId, m.nickname])),
    [members],
  );

  // 살아 있는 메시지만 행으로 만든다. 삭제된 것은 빈 셀로 남긴다 —
  // 스프레드시트에서 값을 지운 것과 구분되지 않는다.
  const rows = useMemo(
    () =>
      messages.map((message) => ({
        id: message.id,
        time: message.deletedAt === null ? formatTime(message.createdAt) : '',
        who: message.deletedAt === null ? (nicknames.get(message.userId) ?? '—') : '',
        body: message.deletedAt === null ? message.body : '',
        muted: message.kind === 'whisper',
        pending: message.pending === true,
      })),
    [messages, nicknames],
  );

  /*
   * 입력 행이 보이도록 스크롤한다.
   *
   * scrollHeight로 끝까지 내리면 뒤에 붙인 빈 행들만 보이고 입력 행과
   * 최근 대화가 화면 위로 사라진다 — 어디에 쳐야 하는지 알 수 없다.
   */
  useLayoutEffect(() => {
    if (stickToBottom.current) {
      inputRowRef.current?.scrollIntoView({ block: 'center' });
    }
  }, [rows.length]);

  // 위장 상태를 메인 프로세스에 알린다. 트레이 툴팁과 배지를 감춰야
  // 화면만 스프레드시트인 상태가 되지 않는다.
  useEffect(() => {
    platform.setStealth(true);
    return () => platform.setStealth(false);
  }, []);

  /**
   * 위로 올려 예전 대화를 읽는 중이면 새 메시지가 와도 끌어내리지 않는다.
   *
   * 기준은 화면 끝이 아니라 입력 행이다. 뒤에 빈 행이 40줄 붙어 있어서
   * 화면 끝을 기준으로 잡으면 늘 "위를 보는 중"으로 판정된다.
   */
  function handleScroll() {
    const element = scrollRef.current;
    const inputRow = inputRowRef.current;
    if (!element || !inputRow) return;

    const rowBottom = inputRow.offsetTop + inputRow.offsetHeight;
    stickToBottom.current = rowBottom - (element.scrollTop + element.clientHeight) < 120;
  }

  function submit(asWhisper: boolean) {
    const body = draft.trim();
    if (body === '' || status !== 'open') return;

    if (asWhisper) sendWhisper(body);
    else sendText(body);
    setDraft('');
  }

  function onInputKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') {
      // 조합 확정용 Enter를 전송으로 읽으면 안 된다(한글·일본어 입력기).
      if (event.nativeEvent.isComposing) return;
      event.preventDefault();
      submit(event.ctrlKey || event.metaKey);
    }
    // Escape는 아래 window 리스너가 처리한다. 여기서 잡으면 안 된다 —
    // 첫 Esc에 포커스를 놓기 때문에 두 번째 Esc는 입력창에 닿지 못한다.
  }

  /**
   * 패닉. Esc를 연달아 두 번 누르면 창을 내린다.
   *
   * window에 걸어야 한다. 첫 Esc가 입력창의 포커스를 놓으므로, 입력창의
   * onKeyDown에만 걸면 두 번째 Esc가 아무 데도 도착하지 않는다 —
   * 패닉이 영영 동작하지 않는 상태가 된다.
   *
   * 웹에서는 창을 최소화할 방법이 없다. 대신 입력 중이던 내용을 지우고
   * 포커스를 놓는다 — 어깨 너머로 보이는 것만이라도 없앤다.
   */
  useEffect(() => {
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();

      const now = Date.now();
      const isSecond = now - lastEscapeAt.current < PANIC_WINDOW_MS;
      lastEscapeAt.current = isSecond ? 0 : now;

      setDraft('');
      inputRef.current?.blur();
      if (isSecond) platform.minimize();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const inputRowNumber = rows.length + 2;

  return (
    <div className="flex h-full flex-col bg-white text-[13px] text-neutral-900">
      <FakeToolbar />

      {/* 수식 입력줄. 선택한 셀 내용을 보여준다. */}
      <div className="flex items-stretch border-b border-neutral-300 bg-neutral-50">
        <span className="w-16 shrink-0 border-r border-neutral-300 px-2 py-1 text-center text-neutral-500">
          C{inputRowNumber}
        </span>
        <span className="w-6 shrink-0 border-r border-neutral-300 py-1 text-center text-neutral-400">
          fx
        </span>
        <span className="min-w-0 flex-1 truncate px-2 py-1 text-neutral-700">{selected ?? ''}</span>
      </div>

      <div ref={scrollRef} onScroll={handleScroll} className="min-h-0 flex-1 overflow-auto">
        <table className="w-full border-collapse table-fixed">
          {/*
            좁은 화면에서는 행 번호·시각·담당을 줄인다. 375px에서 원래 폭
            (10+16+24rem 단위)이면 C열에 두어 글자밖에 남지 않아서, 대화가
            전부 잘려 읽을 수 없다 — 위장은 되지만 채팅이 되지 않는다.
          */}
          <colgroup>
            <col className="w-7 sm:w-10" />
            <col className="w-12 sm:w-16" />
            <col className="w-14 sm:w-24" />
            <col />
          </colgroup>
          <thead className="sticky top-0 z-10">
            <tr className="bg-neutral-100 text-neutral-600">
              <th className="border border-neutral-300 font-normal" />
              {['A', 'B', 'C'].map((label) => (
                <th key={label} className="border border-neutral-300 py-0.5 font-normal">
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <RowHead n={1} />
              {COLUMNS.map((label) => (
                <Cell key={label} bold onSelect={() => setSelected(label)}>
                  {label}
                </Cell>
              ))}
            </tr>

            {rows.map((row, index) => (
              <tr key={row.id}>
                <RowHead n={index + 2} />
                <Cell onSelect={() => setSelected(row.time)}>{row.time}</Cell>
                <Cell onSelect={() => setSelected(row.who)}>{row.who}</Cell>
                <Cell
                  muted={row.muted}
                  pending={row.pending}
                  onSelect={() => setSelected(row.body)}
                >
                  {row.body}
                </Cell>
              </tr>
            ))}

            {/* 입력 행. 겉보기엔 그냥 다음 빈 셀이다. */}
            <tr ref={inputRowRef}>
              <RowHead n={inputRowNumber} />
              <Cell />
              <Cell />
              <td className="border border-neutral-300 p-0">
                <input
                  ref={inputRef}
                  value={draft}
                  onChange={(e) => {
                    setDraft(e.target.value);
                    setSelected(e.target.value);
                  }}
                  onKeyDown={onInputKeyDown}
                  // 키보드가 올라오면 화면이 절반으로 줄어든다. 입력 행이
                  // 그 아래 남아 있으면 자기가 뭘 치는지 볼 수 없다.
                  onFocus={() => inputRowRef.current?.scrollIntoView({ block: 'center' })}
                  enterKeyHint="send"
                  maxLength={MAX_BODY_LENGTH}
                  aria-label="셀 입력"
                  className="w-full bg-transparent px-1.5 py-0.5 outline-none focus:bg-blue-50"
                />
              </td>
            </tr>

            {Array.from({ length: FILLER_ROWS }, (_, i) => (
              <tr key={`filler-${i}`}>
                <RowHead n={inputRowNumber + i + 1} />
                <Cell />
                <Cell />
                <Cell />
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <FakeStatusBar rowCount={rows.length} onReveal={toggleStealth} />
    </div>
  );
}

function RowHead({ n }: { n: number }) {
  return (
    <td className="border border-neutral-300 bg-neutral-100 px-1 py-0.5 text-center text-neutral-500 select-none">
      {n}
    </td>
  );
}

function Cell({
  children,
  bold,
  muted,
  pending,
  onSelect,
}: {
  children?: React.ReactNode;
  bold?: boolean;
  muted?: boolean;
  pending?: boolean;
  onSelect?: () => void;
}) {
  return (
    <td
      onClick={onSelect}
      className={`truncate border border-neutral-300 px-1.5 py-0.5 ${bold ? 'font-medium' : ''} ${
        muted ? 'italic text-neutral-500' : ''
      } ${pending ? 'text-neutral-400' : ''}`}
    >
      {children ?? ' '}
    </td>
  );
}

/** 메뉴 막대. 아무 동작도 하지 않는다 — 눈속임 전용이다. */
function FakeToolbar() {
  return (
    <div className="shrink-0 select-none border-b border-neutral-300 bg-neutral-50">
      <div className="flex items-center gap-3 px-2 py-1 text-neutral-600">
        <span className="font-medium text-neutral-800">분기_실적_취합_v3.xlsx</span>
        {['파일', '홈', '삽입', '데이터', '검토', '보기'].map((label) => (
          <span key={label} className="hidden text-neutral-500 sm:inline">
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}

function FakeStatusBar({ rowCount, onReveal }: { rowCount: number; onReveal: () => void }) {
  return (
    <div className="flex shrink-0 items-center gap-3 border-t border-neutral-300 bg-neutral-50 px-2 py-1 text-[11px] text-neutral-500 select-none">
      {['1분기', '2분기', '3분기'].map((label) => (
        <span key={label} className="hidden sm:inline">
          {label}
        </span>
      ))}
      <span className="rounded-t bg-white px-2 font-medium text-neutral-800">취합</span>

      <span className="ml-auto hidden sm:inline">준비</span>
      <span>{rowCount}개 항목</span>

      {/*
        위장을 푸는 통로. 셀처럼 생겨서 눈에 띄지 않는다.
        키보드로도 닿아야 하므로 aria-label은 남긴다.

        모바일에서는 이게 유일한 탈출구다 — 보스키가 없다. 그래서 보이는
        크기는 3px 그대로 두고 눌리는 영역만 키운다. 눈에 띄게 만들면
        위장이 깨지고, 작게 두면 손가락으로 못 누른다.
      */}
      <button
        type="button"
        onClick={onReveal}
        aria-label="스텔스 해제"
        title="Ctrl+Shift+X"
        className="-m-2.5 flex cursor-default items-center justify-center p-2.5"
      >
        <span className="block h-3 w-3 rounded-sm border border-neutral-300 bg-neutral-100" />
      </button>
    </div>
  );
}

const timeFormat = new Intl.DateTimeFormat('ko-KR', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

function formatTime(epochMs: number): string {
  return timeFormat.format(new Date(epochMs));
}
